<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import { format } from 'date-fns'; // Import date-fns for formatting

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  columns: {
    type: Array,
    default: () => [],
  },
  filteredResults: {
    type: Object,
    default: () => ({ total: 0, stages: {} }),
  },
  currentFunnel: {
    type: Object,
    default: null,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  size: {
    type: String,
    default: 'medium',
  },
  initialFilters: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const emit = defineEmits(['apply', 'close']);

const filters = ref({
  priority: [],
  value: {
    min: null,
    max: null,
  },
  agent_id: null,
  date: {
    start: null,
    end: null,
  },
});

// --- Preset State --- 
const savedPresets = ref({});
const newPresetName = ref('');
const appliedPresetName = ref('');
const PRESET_STORAGE_KEY = 'kanban_filter_presets';

// Function to check if current filters match any preset
const checkAppliedPreset = (currentFilters) => {
    if (!currentFilters) return '';
    for (const name in savedPresets.value) {
        // Deep comparison, ensure structure matches
        const preset = savedPresets.value[name];
        const current = JSON.parse(JSON.stringify(currentFilters)); // Deep copy for safety
        // Normalize empty values for comparison if necessary
        preset.priority = preset.priority || [];
        preset.value = preset.value || {};
        preset.date = preset.date || {};
        current.priority = current.priority || [];
        current.value = current.value || {};
        current.date = current.date || {};

        if (JSON.stringify(preset) === JSON.stringify(current)) {
            return name;
        }
    }
    return '';
};

// --- Load Presets on Mount ---
const loadPresets = () => {
  const storedPresets = localStorage.getItem(PRESET_STORAGE_KEY);
  if (storedPresets) {
    try {
      savedPresets.value = JSON.parse(storedPresets);
    } catch (e) {
      console.error('Error loading filter presets:', e);
      savedPresets.value = {};
    }
  }
};

// Watch for the modal becoming visible or initialFilters changing
watch(() => [props.show, props.initialFilters], ([newShow, newInitialFilters]) => {
    if (newShow) {
        // When modal opens, load the initial state from the column
        if (newInitialFilters) {
            filters.value = JSON.parse(JSON.stringify(newInitialFilters)); // Deep copy
            
            // Ensure agent_id is a number if it's not null
            if (filters.value.agent_id !== null && filters.value.agent_id !== undefined) {
                filters.value.agent_id = Number(filters.value.agent_id);
            }
        } else {
            // Reset to default if no initial filters provided
            filters.value = {
                priority: [],
                value: { min: null, max: null },
                agent_id: null,
                date: { start: null, end: null },
            };
        }
        // Check if the loaded filters match a preset
        appliedPresetName.value = checkAppliedPreset(filters.value);
    }
}, { immediate: true, deep: true });

// Watch for changes to agent_id to ensure it's always a number when selected
watch(() => filters.value.agent_id, (newValue) => {
    console.log('[DEBUG] agent_id alterado para:', newValue, 'tipo:', typeof newValue);
    if (newValue !== null && newValue !== undefined) {
        // Convert to number to ensure consistent types
        filters.value.agent_id = Number(newValue);
        console.log('[DEBUG] agent_id convertido para número:', filters.value.agent_id, 'tipo:', typeof filters.value.agent_id);
    }
});

onMounted(() => {
  loadPresets();
  if (store.getters['agents/getAgents'].length === 0) {
    store.dispatch('agents/get');
  }
});
// ---------------------

const priorityOptions = computed(() => [
  {
    label: 'Alta',
    value: 'high',
    colors: {
      border: filters.value.priority.includes('high')
        ? 'border-ruby-500 dark:border-ruby-400'
        : 'border-slate-300 dark:border-slate-600',
      text: filters.value.priority.includes('high')
        ? 'text-ruby-700 dark:text-ruby-300'
        : 'text-slate-700 dark:text-slate-300',
      hover: 'hover:bg-slate-100 dark:hover:bg-slate-700',
      selected:
        'bg-ruby-50 dark:bg-ruby-800 border-ruby-500 dark:border-ruby-400',
    },
  },
  {
    label: 'Média',
    value: 'medium',
    colors: {
      border: filters.value.priority.includes('medium')
        ? 'border-yellow-500 dark:border-yellow-400'
        : 'border-slate-300 dark:border-slate-600',
      text: filters.value.priority.includes('medium')
        ? 'text-yellow-700 dark:text-yellow-300'
        : 'text-slate-700 dark:text-slate-300',
      hover: 'hover:bg-slate-100 dark:hover:bg-slate-700',
      selected:
        'bg-yellow-50 dark:bg-yellow-800 border-yellow-500 dark:border-yellow-400',
    },
  },
  {
    label: 'Baixa',
    value: 'low',
    colors: {
      border: filters.value.priority.includes('low')
        ? 'border-green-500 dark:border-green-400'
        : 'border-slate-300 dark:border-slate-600',
      text: filters.value.priority.includes('low')
        ? 'text-green-700 dark:text-green-300'
        : 'text-slate-700 dark:text-slate-300',
      hover: 'hover:bg-slate-100 dark:hover:bg-slate-700',
      selected:
        'bg-green-50 dark:bg-green-800 border-green-500 dark:border-green-400',
    },
  },
  {
    label: 'Urgente',
    value: 'urgent',
    colors: {
      border: filters.value.priority.includes('urgent')
        ? 'border-ruby-500 dark:border-ruby-400'
        : 'border-slate-300 dark:border-slate-600',
      text: filters.value.priority.includes('urgent')
        ? 'text-ruby-700 dark:text-ruby-300'
        : 'text-slate-700 dark:text-slate-300',
      hover: 'hover:bg-slate-100 dark:hover:bg-slate-700',
      selected:
        'bg-ruby-50 dark:bg-ruby-800 border-ruby-500 dark:border-ruby-400',
    },
  },
]);

const agentsLoaded = ref(false);

const agents = computed(() => {
  return store.getters['agents/getAgents'] || [];
});

// --- Helper to get Agent Name by ID ---
const getAgentName = (agentId) => {
  if (!agentId) return '';
  const agent = agents.value.find(a => a.id === agentId);
  return agent ? agent.name : `ID: ${agentId}`;
};

// --- Helper Function for Preset Summary ---
const getPresetSummary = (presetFilters) => {
  if (!presetFilters) return '';
  const parts = [];

  // Priority
  if (presetFilters.priority?.length) {
    parts.push(`Prioridade: ${presetFilters.priority.join(', ')}`);
  }

  // Value
  const { min, max } = presetFilters.value || {};
  if (min !== undefined && min !== null && max !== undefined && max !== null) {
    parts.push(`Valor: ${min}-${max}`);
  } else if (min !== undefined && min !== null) {
    parts.push(`Valor: >= ${min}`);
  } else if (max !== undefined && max !== null) {
    parts.push(`Valor: <= ${max}`);
  }

  // Agent
  if (presetFilters.agent_id) {
    parts.push(`Agente: ${getAgentName(presetFilters.agent_id)}`);
  }

  // Date
  const { start, end } = presetFilters.date || {};
  if (start && end) {
    try {
       parts.push(`Data: ${format(new Date(start), 'dd/MM')} - ${format(new Date(end), 'dd/MM')}`);
    } catch { /* Handle invalid date strings */ }
  } else if (start) {
     try {
       parts.push(`Data: >= ${format(new Date(start), 'dd/MM/yy')}`);
    } catch { /* Handle invalid date strings */ }
  } else if (end) {
    try {
       parts.push(`Data: <= ${format(new Date(end), 'dd/MM/yy')}`);
    } catch { /* Handle invalid date strings */ }
  }

  return parts.join('. ') || 'Nenhum filtro ativo'; // Return summary or default text
};
// ---------------------------------------

const getStageColor = stageName => {
  if (!props.currentFunnel?.stages) return '#64748B';

  const stage = Object.values(props.currentFunnel.stages).find(
    s => s.name === stageName
  );

  if (!stage) {
    const stageById = Object.values(props.currentFunnel.stages).find(
      s => s.id === stageName
    );
    return stageById?.color || '#64748B';
  }

  return stage.color || '#64748B';
};

const hasActiveFilters = computed(() => {
  const hasFilters = Boolean(
    filters.value.priority.length > 0 ||
      filters.value.value.min ||
      filters.value.value.max ||
      filters.value.agent_id ||
      filters.value.date.start ||
      filters.value.date.end
  );

  return hasFilters && props.filteredResults.total > 0;
});

const handleApply = () => {
  console.log('[DEBUG] Aplicando filtro, agent_id original:', filters.value.agent_id, 'tipo:', typeof filters.value.agent_id);
  
  // Ensure agent_id is a number before sending
  const filtersToApply = JSON.parse(JSON.stringify(filters.value));
  if (filtersToApply.agent_id !== null && filtersToApply.agent_id !== undefined) {
    filtersToApply.agent_id = Number(filtersToApply.agent_id);
    console.log('[DEBUG] agent_id convertido antes de emitir:', filtersToApply.agent_id, 'tipo:', typeof filtersToApply.agent_id);
  }
  
  // Emit the current filters state (deep copy)
  emit('apply', filtersToApply);
  emit('close');
};

const handleClear = () => {
  const clearedFilters = {
    priority: [],
    value: { min: null, max: null },
    agent_id: null,
    date: { start: null, end: null },
  };
  filters.value = JSON.parse(JSON.stringify(clearedFilters));
  appliedPresetName.value = '';
  // Emit the cleared state so the column can remove it from localStorage
  emit('apply', JSON.parse(JSON.stringify(clearedFilters))); 
  // Optionally close immediately or let user click Apply
  // emit('close'); 
};

// --- Preset Functions ---
const saveCurrentPreset = () => {
  const name = newPresetName.value.trim();
  if (!name) {
    alert('Por favor, insira um nome para a predefinição.');
    return;
  }
  if (savedPresets.value[name]) {
    if (!confirm(`Já existe uma predefinição com o nome "${name}". Deseja sobrescrevê-la?`)) {
      return;
    }
  }
  // Ensure we save with consistent types
  const filtersToSave = JSON.parse(JSON.stringify(filters.value));
  if (filtersToSave.agent_id !== null && filtersToSave.agent_id !== undefined) {
    filtersToSave.agent_id = Number(filtersToSave.agent_id);
  }
  
  savedPresets.value[name] = filtersToSave;
  localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(savedPresets.value));
  newPresetName.value = '';
  appliedPresetName.value = name; // Mark the new preset as applied locally
};

const applyPreset = name => {
  if (savedPresets.value[name]) {
    filters.value = JSON.parse(JSON.stringify(savedPresets.value[name]));
    appliedPresetName.value = name;
    // User still needs to click "Apply" to confirm
  } else {
    console.warn(`Preset "${name}" not found.`);
  }
};

const deletePreset = name => {
  if (savedPresets.value[name]) {
    if (confirm(`Tem certeza que deseja deletar a predefinição "${name}"?`)) {
      // Check if the preset being deleted is the currently applied one
      const deletingApplied = appliedPresetName.value === name;
      
      delete savedPresets.value[name];
      localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(savedPresets.value));
      
      // If the deleted preset was the one applied, clear the applied name
      if (deletingApplied) {
         appliedPresetName.value = '';
         // Optionally clear the actual filters as well, or leave them as they were?
         // For now, just clear the name, filters remain until user action
      }
    }
  } else {
    console.warn(`Preset "${name}" not found for deletion.`);
  }
};
</script>

<template>
  <Teleport to="body">
    <Modal
      v-if="show"
      :show="show"
      :size="props.size"
      :show-close-button="false"
      @close="$emit('close')"
    >
      <div class="p-4">
        <header class="mb-3 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-medium">
              {{ t('KANBAN.FILTER_ITEMS') }}
            </h3>
            <div
              v-if="hasActiveFilters && filteredResults.total > 0"
              class="flex gap-1"
            >
              <div class="results-tag">
                {{ filteredResults.total }} {{ t('KANBAN.FILTER.RESULTS') }}
              </div>
              <div
                v-for="(count, stageName) in filteredResults.stages"
                :key="stageName"
                class="results-tag"
                :style="{ backgroundColor: getStageColor(stageName) }"
              >
                <span class="stage-name">{{ stageName }}</span>
                <span class="count-badge">{{ count }}</span>
              </div>
            </div>
          </div>
          <woot-button
            variant="clear"
            size="small"
            color-scheme="secondary"
            @click="$emit('close')"
          >
            <fluent-icon icon="dismiss" size="16" />
          </woot-button>
        </header>

        <div class="filter-sections grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Filter Controls -->
          <div class="space-y-2 border-r md:border-r-slate-200 md:dark:border-r-slate-700 md:pr-4">
          <!-- Prioridade -->
          <div class="filter-group">
            <label class="text-sm font-medium mb-1 block">
              {{ t('KANBAN.FORM.PRIORITY.LABEL') }}
            </label>
            <div class="flex flex-wrap gap-1">
              <button
                v-for="option in priorityOptions"
                :key="option.value"
                :class="[
                    'border px-3 py-1 text-sm font-medium rounded-lg transition-colors',
                  filters.priority.includes(option.value)
                    ? option.colors.selected
                    : [option.colors.border, option.colors.hover],
                  option.colors.text,
                ]"
                @click="
                  filters.priority = filters.priority.includes(option.value)
                    ? filters.priority.filter(p => p !== option.value)
                    : [...filters.priority, option.value]
                "
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <!-- Valor -->
          <div class="filter-group">
            <label class="text-sm font-medium mb-1 block">
              {{ t('KANBAN.VALUE.LABEL') }}
            </label>
            <div class="flex gap-2">
              <woot-input
                v-model="filters.value.min"
                type="number"
                :placeholder="t('KANBAN.VALUE.MIN')"
                class="flex-1"
              />
              <woot-input
                v-model="filters.value.max"
                type="number"
                :placeholder="t('KANBAN.VALUE.MAX')"
                class="flex-1"
              />
            </div>
          </div>

          <!-- Agente Responsável -->
          <div class="filter-group">
            <label class="text-sm font-medium mb-1 block">
              {{ t('KANBAN.AGENT.LABEL') }}
            </label>
            <div class="relative">
              <select
                v-model="filters.agent_id"
                class="w-full rounded-lg border border-slate-300 dark:border-slate-700 p-2"
              >
                <option :value="null">{{ t('KANBAN.FILTER.AGENT.ALL') }}</option>
                <option :value="-1">{{ t('KANBAN.FILTER.AGENT.NONE') || 'Não atribuído' }}</option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Data de Criação -->
          <div class="filter-group">
            <label class="text-sm font-medium mb-1 block">
              {{ t('KANBAN.FILTER.DATE.LABEL') }}
            </label>
            <div class="flex gap-2">
              <woot-input
                v-model="filters.date.start"
                type="date"
                :placeholder="t('KANBAN.FILTER.DATE.START')"
                class="flex-1"
              />
              <woot-input
                v-model="filters.date.end"
                type="date"
                :placeholder="t('KANBAN.FILTER.DATE.END')"
                class="flex-1"
              />
              </div>
            </div>
          </div>

          <!-- Preset Management -->
          <div class="space-y-3">
             <h4 class="text-md font-medium border-b border-slate-200 dark:border-slate-700 pb-1 mb-2">Predefinições</h4>
            
             <!-- Save Current -->
             <div class="space-y-1">
                <label class="text-sm font-medium block">Salvar filtro atual</label>
                <div class="flex gap-1">
                   <woot-input
                      v-model="newPresetName"
                      type="text"
                      placeholder="Nome da predefinição"
                      class="flex-1"
                      @keyup.enter="saveCurrentPreset"
                   />
                   <woot-button
                      variant="smooth"
                      size="small"
                      icon="save"
                      color-scheme="secondary"
                      :disabled="!newPresetName.trim()"
                      @click="saveCurrentPreset"
                   />
                </div>
             </div>

             <!-- Saved Presets List -->
             <div class="space-y-1">
                <label class="text-sm font-medium block">Predefinições salvas</label>
                 <div v-if="Object.keys(savedPresets).length === 0" class="text-xs text-slate-500 dark:text-slate-400 p-2 text-center">
                   Nenhuma predefinição salva.
                 </div>
                <ul v-else class="max-h-48 overflow-y-auto space-y-1 pr-1">
                   <li
                      v-for="(presetFilters, name) in savedPresets"
                      :key="name"
                      class="flex justify-between items-center p-1.5 rounded-md flex-wrap"
                      :class="{
                         'bg-woot-50 dark:bg-woot-800/50': appliedPresetName === name,
                         'hover:bg-slate-100 dark:hover:bg-slate-700/50': appliedPresetName !== name
                      }"
                   >
                     <div class="flex-1 min-w-0 mr-2">
                        <span class="text-sm font-medium block truncate" :title="name">{{ name }}</span>
                        <span class="text-xs text-slate-500 dark:text-slate-400 block truncate" :title="getPresetSummary(presetFilters)">
                           {{ getPresetSummary(presetFilters) }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1 flex-shrink-0">
                         <woot-button
                            variant="clear"
                            size="small"
                            color-scheme="secondary"
                            title="Aplicar"
                            @click="applyPreset(name)"
                         >
                            <fluent-icon icon="play-circle" size="14" />
                         </woot-button>
                         <woot-button
                            variant="clear"
                            size="small"
                            color-scheme="alert"
                            title="Deletar"
                            @click="deletePreset(name)"
                         >
                            <fluent-icon icon="delete" size="14" />
                         </woot-button>
                      </div>
                   </li>
                </ul>
            </div>
          </div>
        </div>

        <footer
          class="flex justify-end space-x-2 pt-3 mt-3 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button
            variant="clear"
            color-scheme="secondary"
            @click="handleClear"
          >
            {{ t('KANBAN.FORM.CLEAR') }}
          </woot-button>
          <woot-button
            variant="solid"
            color-scheme="primary"
            @click="handleApply"
          >
            {{ t('KANBAN.FORM.APPLY') }}
          </woot-button>
        </footer>
      </div>
    </Modal>
  </Teleport>
</template>

<style lang="scss" scoped>
.filter-group {
  @apply p-2 bg-slate-50 dark:bg-slate-800 rounded-lg;
}

.loading-spinner {
  @apply w-4 h-4 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin;
}

.results-tag {
  @apply flex items-center px-2 py-0.5 text-xs font-medium rounded-full
    bg-woot-500 text-white whitespace-nowrap gap-1;

  &:first-child {
    @apply bg-slate-600 dark:bg-slate-700;
  }

  .stage-name {
    @apply text-[10px];
  }

  .count-badge {
    @apply bg-white/20 px-1.5 rounded-full text-[10px] min-w-[18px] text-center;
  }
}

// Adjust modal layout for presets
.filter-sections {
  max-height: 70vh; // Prevent modal from becoming too tall
  overflow-y: hidden; // Hide main overflow, handle inside sections

  & > div {
    overflow-y: auto; // Allow individual sections to scroll if needed
  }
}

// Style for the preset list
ul {
  scrollbar-width: thin; 
  scrollbar-color: var(--w-500) var(--s-50);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--s-50);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--w-500);
    border-radius: 3px;
  }
}
</style>
